import './fristen.css';
import { useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { Dialog } from '../../components/Dialog';
import { fmtDate, today } from '../../lib/format';
import { eurC, parseEuro } from '../../lib/money';
import { useToast } from '../../lib/toast';
import type { KanzleiFiling, PeriodInfo, PeriodRef } from './types';

// UStVA als übermittelt eintragen (bzw. Berichtigung hochladen): Übertragungsprotokoll des Steuerberaters, Datum und
// Zahllast laut Protokoll. Der Mandant sieht beides in seiner App und bekommt eine Benachrichtigung.

const iso = (d: { year: number; month: number; day: number }) =>
  `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;

// erster Tag nach dem Zeitraum (frühestes Übermittlungsdatum)
export function firstAfter(p: PeriodRef): string {
  const last = p.kind === 'J' ? 12 : p.kind === 'Q' ? p.n * 3 : p.n;
  return last === 12 ? `${p.year + 1}-01-01` : `${p.year}-${String(last + 1).padStart(2, '0')}-01`;
}

export function FilingDialog({
  clientId,
  clientName,
  period,
  filing,
  onClose,
}: {
  clientId: string;
  clientName: string;
  period: PeriodRef & { title: string; deadline?: string };
  // gesetzt: Berichtigung zu dieser Übermittlung
  filing?: KanzleiFiling;
  onClose(): void;
}) {
  const toast = useToast();
  const todayIso = iso(today());
  const [file, setFile] = useState<File | null>(null);
  const [filedOn, setFiledOn] = useState(todayIso);
  const [amount, setAmount] = useState(
    filing?.amount != null ? filing.amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const parsed = parseEuro(amount);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return setError('Bitte das Protokoll auswählen.');
    if (parsed === undefined) return setError('Bitte einen gültigen Betrag eintragen, z. B. 2.950,40 oder −120,00.');
    setError(null);
    setBusy(true);
    const fd = new FormData();
    // zuerst die Angaben, dann die Datei (so prüft der Server, bevor er speichert)
    fd.append(
      'data',
      JSON.stringify(
        filing ? { filedOn, amount: parsed } : { kind: period.kind, year: period.year, n: period.n, filedOn, amount: parsed },
      ),
    );
    fd.append('file', file, file.name);
    try {
      if (filing) {
        await api.post(`/api/kanzlei/ustva/filing/${filing.id}/files`, fd);
        toast(`Berichtigung gespeichert · ${period.title} · der Mandant sieht sie in der App`);
      } else {
        const r = await api.post<{ next: PeriodInfo | null }>(`/api/kanzlei/ustva/${clientId}`, fd);
        toast(`${period.title} eingetragen${r.next ? ` · nächste Frist: ${r.next.title} bis ${fmtDate(r.next.deadline)}` : ''}`);
      }
      onClose();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} title={`${filing ? 'Berichtigung' : 'Übermittelt'}: ${period.title}`} className="ustdlg">
      <p className="note">
        {clientName}
        {period.deadline ? ` · Frist ${fmtDate(period.deadline)}` : ''}
        {filing
          ? ` · bisher übermittelt am ${fmtDate(filing.filedOn)}${filing.amount !== null ? `, ${amountLabel(filing.amount)}` : ''}`
          : ''}
      </p>
      <form className="form" onSubmit={submit} noValidate>
        <label>
          {filing ? 'Protokoll der Berichtigung (PDF oder Foto) *' : 'Übertragungsprotokoll (PDF oder Foto) *'}
          <input
            type="file"
            accept="application/pdf,image/*"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setError(null);
            }}
          />
        </label>
        <div className="row ust-two">
          <label>
            Übermittelt am *
            <input
              type="date"
              value={filedOn}
              min={firstAfter(period)}
              max={todayIso}
              required
              onChange={(e) => setFiledOn(e.target.value)}
            />
          </label>
          <label>
            Zahllast laut Protokoll (€)
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              placeholder="z. B. 2.950,40"
              aria-describedby="ust-amt-hint"
              onChange={(e) => {
                setAmount(e.target.value);
                setError(null);
              }}
            />
          </label>
        </div>
        <p className="note" id="ust-amt-hint">
          Erstattung mit Minus eintragen, z. B. −120,00. Leer lassen, wenn der Betrag nur im Protokoll stehen soll.
          {parsed != null && ` Der Mandant sieht: ${amountLabel(parsed)}.`}
        </p>
        {error && (
          <p className="note ust-err" role="alert">
            {error}
          </p>
        )}
        <div className="dlg-actions">
          <button type="submit" className="btn dark" disabled={busy}>
            {filing ? 'Berichtigung speichern' : 'Eintragen'}
          </button>
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
        </div>
        <p className="note">
          Die UStVA übermittelt der Steuerberater. Hier kommt sein Protokoll hinein; {clientName} bekommt eine Benachrichtigung
          und sieht Protokoll und Betrag in der App.
          {!filing && ' Danach steht die nächste Frist da.'}
        </p>
      </form>
    </Dialog>
  );
}

export function amountLabel(v: number): string {
  return v < 0 ? `Erstattung ${eurC(-v)}` : v === 0 ? 'nichts zu zahlen' : `Zahllast ${eurC(v)}`;
}
